import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';

import { Instant } from '@js-joda/core';

import { DsLoggingService } from '../../shared/services/ds-logging.service';
import { Clock } from '../clock/clock';
import { RefreshTier } from '../config/enums';
import { SignageConfigurationModel } from '../config/models';
import { SignageDataSource } from '../data/signage-data-source';
import { ContentKind } from '../slides/enums';
import { RuntimeSlideModel } from '../slides/models';
import { CONTENT_PROVIDERS } from './content-provider';
import { FeedStatus, PresentationSlot } from './enums';
import { ContentSnapshotModel, FeedHealthModel, FeedRecords } from './models';
import { ContentProvider } from './interfaces';

// Holds one bucket of slides per content provider, refreshed per tier and readable as one playlist.
@Injectable()
export class ContentRegistryService implements OnDestroy {
  private readonly source = inject(SignageDataSource);
  private readonly clock = inject(Clock);
  private readonly logging = inject(DsLoggingService);
  private readonly providers = inject(CONTENT_PROVIDERS, { optional: true }) || [];

  private readonly records = signal<FeedRecords>(new Map());
  private readonly healthState = signal<Readonly<Partial<Record<ContentKind, FeedHealthModel>>>>({});
  private readonly active = signal<readonly ContentProvider[]>([]);

  // One controller per tier, so a new refresh of that tier cancels the one still running.
  private readonly pending = new Map<RefreshTier, AbortController>();

  // Bumped on every channel change; a fetch that started under an older generation is discarded.
  private generation = 0;
  private config?: SignageConfigurationModel;

  public readonly slides = computed(() => this.collect(PresentationSlot.Playlist));
  public readonly overlays = computed(() => this.collect(PresentationSlot.Overlay));
  public readonly interrupts = computed(() => this.collect(PresentationSlot.Interrupt));
  public readonly feedHealth = this.healthState.asReadonly();
  public readonly health = this.feedHealth;

  public activate(config: SignageConfigurationModel): void {
    this.cancelPending();

    // Staying on the same channel keeps what is already on screen; a new channel starts empty.
    const sameChannel = this.config && this.channelKey(this.config) === this.channelKey(config);
    this.config = config;

    const providers = this.enabledProviders(config);
    this.active.set(providers);

    const records = new Map<ContentKind, readonly RuntimeSlideModel[]>();
    const health: Partial<Record<ContentKind, FeedHealthModel>> = {};

    for (const provider of providers) {
      records.set(provider.kind, sameChannel ? this.records().get(provider.kind) || [] : []);
      health[provider.kind] = sameChannel && this.healthState()[provider.kind]
        ? this.healthState()[provider.kind]
        : { kind: provider.kind, failures: 0, status: FeedStatus.Idle };
    }

    this.records.set(records);
    this.healthState.set(health);
  }

  public async refresh(tier: RefreshTier, signal: AbortSignal): Promise<void> {
    if (!this.config || signal.aborted) {
      return;
    }

    this.pending.get(tier)?.abort();

    const controller = new AbortController();
    const abort = (): void => controller.abort();
    signal.addEventListener('abort', abort, { once: true });
    this.pending.set(tier, controller);

    const generation = this.generation;
    const config = this.config;
    const due = this.active().filter(provider => provider.tier === tier);

    try {
      // One failing feed must not cancel the others, so every provider settles on its own.
      await Promise.allSettled(due.map(provider => this.refreshOne(provider, config, tier, controller, generation)));
    } finally {
      signal.removeEventListener('abort', abort);

      if (this.pending.get(tier) === controller) {
        this.pending.delete(tier);
      }
    }
  }

  public isRelevant(slide: RuntimeSlideModel, now: Instant = this.clock.now()): boolean {
    if (slide.meta.expiresAt !== undefined && slide.meta.expiresAt <= now.toEpochMilli()) {
      return false;
    }

    const provider = this.active().find(item => item.kind === slide.kind);

    return !!provider && (!provider.isStillRelevant || provider.isStillRelevant(slide, now));
  }

  public snapshot(): ContentSnapshotModel {
    return {
      version: 1, channel: this.config ? this.channelKey(this.config) : '',
      feeds: this.active().map(provider => ({
        kind: provider.kind, slides: this.records().get(provider.kind) || [], health: this.healthState()[provider.kind]
      }))
    };
  }

  public restore(snapshot: ContentSnapshotModel): boolean {
    if (!this.config || snapshot?.version !== 1 || snapshot.channel !== this.channelKey(this.config)) {
      return false;
    }

    const records = new Map(this.records());
    const health = { ...this.healthState() };

    for (const provider of this.active()) {
      const feed = snapshot.feeds.find(item => item.kind === provider.kind);

      if (!feed) {
        continue;
      }

      records.set(provider.kind, feed.slides.filter(slide => slide.kind === provider.kind && this.isRelevant(slide)));
      health[provider.kind] = { ...feed.health, kind: provider.kind, status: FeedStatus.Idle };
    }

    this.records.set(records);
    this.healthState.set(health);

    return true;
  }

  public ngOnDestroy(): void {
    this.cancelPending();
  }

  private async refreshOne(provider: ContentProvider, config: SignageConfigurationModel, tier: RefreshTier,
                           controller: AbortController, generation: number): Promise<void> {
    const previous = this.healthState()[provider.kind];
    this.setHealth(provider.kind, { ...previous, status: FeedStatus.Refreshing });

    try {
      const items = await provider.fetch({ config, source: this.source, clock: this.clock, signal: controller.signal });

      if (!this.canLand(tier, controller, generation)) {
        return;
      }

      const slides = provider.toSlides(items, { config, clock: this.clock });
      const next = new Map(this.records());
      next.set(provider.kind, slides.filter(slide => this.isRelevant(slide)));

      this.records.set(next);
      this.setHealth(provider.kind, {
        kind: provider.kind, status: FeedStatus.Healthy, failures: 0, lastSuccess: this.clock.now().toEpochMilli()
      });
    } catch {
      if (!this.canLand(tier, controller, generation)) {
        return;
      }

      this.setHealth(provider.kind, { ...previous, status: FeedStatus.Failed, failures: previous.failures + 1 });
      this.logging.trackEvent('feed_failed', { feed: provider.kind, tier });
    }
  }

  private enabledProviders(config: SignageConfigurationModel): readonly ContentProvider[] {
    const providers = this.providers.filter(provider => provider.isEnabled(config)).sort((a, b) => a.order - b.order);

    // Two providers of one kind would overwrite each other's bucket; failing loudly beats losing a feed silently.
    if (new Set(providers.map(provider => provider.kind)).size !== providers.length) {
      throw new Error('Duplicate signage content kind');
    }

    return providers;
  }

  private collect(presentation: ContentProvider['presentation']): readonly RuntimeSlideModel[] {
    return this.active().filter(provider => provider.presentation === presentation)
      .flatMap(provider => this.records().get(provider.kind) || []);
  }

  // A result may only land if nothing newer replaced it: same channel, same tier run, not cancelled.
  private canLand(tier: RefreshTier, controller: AbortController, generation: number): boolean {
    return !controller.signal.aborted && this.generation === generation && this.pending.get(tier) === controller;
  }

  private cancelPending(): void {
    ++this.generation;
    this.pending.forEach(controller => controller.abort());
    this.pending.clear();
  }

  private setHealth(kind: ContentKind, health: FeedHealthModel): void {
    this.healthState.update(previous => ({ ...previous, [kind]: health }));
  }

  private channelKey(config: SignageConfigurationModel): string {
    return JSON.stringify([config.channel.providerUuid, config.channel.id, config.channel.imagesSet || '']);
  }
}
